import { ContextWindow, useDocking } from "@asup/context-menu";
import { AsyncButton, checkName, ControlTypeAhead, TypeAheadItem } from "aic";
import { useTlfContext } from "contexts/AppSettingsContext";
import { getVariantPatchContext } from "contexts/tlf/getVariantPatchContext";
import { CANCEL_VARIANT_PATCH_CREATION, CREATE_VARIANT_PATCH } from "contexts/tlf/interface";
import { ReactNode, useMemo, useState } from "react";
import { Container } from "react-bootstrap";

/**
 * Context-owned window that creates a named patch and manages its own visibility.
 *
 * @returns The patch creation window
 */
export const CreatePatchWindow = (): ReactNode => {
  const docking = useDocking();
  const tlfContext = useTlfContext();
  const [name, setName] = useState("");
  const [isValidated, setIsValidated] = useState(false);
  const { currentMaster } = getVariantPatchContext(tlfContext);
  const existingNames = Object.keys(currentMaster?.allVariantPatches ?? {});
  const visible =
    tlfContext.showCreateVariantPatchWindow ||
    (tlfContext.variantCreationView && tlfContext.activeVariantPatchName === null);
  const disabledReason = tlfContext.readOnly
    ? "You do not have permission to edit this TLF"
    : undefined;
  const options = useMemo<TypeAheadItem[]>(
    () => existingNames.map((value) => ({ value })),
    [existingNames],
  );
  const trimmedName = name.trim();
  const reason =
    (trimmedName === "" ? "Enter a patch name" : undefined) ??
    (existingNames.includes(trimmedName) ? "This patch name already exists" : undefined);

  /**
   * Closes patch creation through the TLF reducer.
   */
  const closeWindow = (): void => {
    tlfContext.dispatch({
      operation: CANCEL_VARIANT_PATCH_CREATION,
      calledFrom: "CreatePatchWindow:cancel",
    });
  };

  /**
   * Dispatches patch creation and clears the local name-validation input.
   */
  const createPatch = (): void => {
    tlfContext.dispatch({
      operation: CREATE_VARIANT_PATCH,
      patchName: trimmedName,
      calledFrom: "CreatePatchWindow:create",
    });
    setName("");
    setIsValidated(false);
    // Show the patch window and push it to the top
    docking.showWindowById("tlf-ars-patches-window");
  };

  return (
    <ContextWindow
      id="create-ars-patch-window"
      onClose={closeWindow}
      title="Create new patch"
      visible={visible}
    >
      <Container className="p-3">
        <ControlTypeAhead
          id="create-ars-patch-name"
          label="Patch name"
          labelWidth={3}
          value={name}
          setValue={setName}
          availableList={options}
          validCondition={checkName}
          validationReturn={setIsValidated}
          placeHolder="Enter a unique patch name"
        />
        <div className="d-flex justify-content-end gap-2">
          <AsyncButton
            id="create-ars-patch-cancel"
            onClick={closeWindow}
            variant="secondary"
          >
            Cancel
          </AsyncButton>
          {disabledReason === undefined && (
            <AsyncButton
              id="create-ars-patch-confirm"
              disabled={!isValidated}
              onClick={createPatch}
              title={reason}
              variant="primary"
            >
              Create patch
            </AsyncButton>
          )}
        </div>
      </Container>
    </ContextWindow>
  );
};

CreatePatchWindow.displayName = "CreatePatchWindow";
